import {
  Autocomplete,
  Button,
  Checkbox,
  CircularProgress,
  TextField,
  Typography,
} from "@mui/material";
import MButton from "components/momentum/MButton";
import {
  DeRiskMultipleController,
  HistoryListRenderer,
  NoteHistoryWrapper,
  YNOptions,
} from "components/summary/success-plan";
import RiskSelectController from "components/summary/success-plan/components/workflow/RiskSelectController";
import TinyMCEController from "components/summary/success-plan/components/workflow/TinyMCEController";
import { useSpContext } from "components/summary/success-plan/context/useSpContext";
import { SuccessPlanState } from "components/summary/success-plan/state/successPlan.types";
import { FEATURE_KEY } from "constant";
import { useAxios } from "myAxios";
import { AuthProvider } from "pages";
import { useContext, useMemo, useState } from "react";
import { Controller, UseFormReturn } from "react-hook-form";
import styles from "./SuccessPlanWorkflow.module.scss";
import { remark } from "remark";
import remarkHtml from "remark-html";
import remarkGfm from "remark-gfm";

type CustomerAssessmentStepProps = {
  spState: SuccessPlanState;
  form: UseFormReturn<any>;
};

export async function markdownToHtml(md: string) {
  const result = await remark().use(remarkGfm).use(remarkHtml).process(md);

  return result.toString();
}

const CustomerAssessmentStep = ({ spState, form }: CustomerAssessmentStepProps) => {
  const { features } = useContext(AuthProvider);
  const { riskItems } = useSpContext();
  const { spData } = spState;
  const histories = spData.histories || [];
  const [togglePreviousNotes, setTogglePreviousNotes] = useState(false);
  const [toggleCSENotes, setToggleCSENotes] = useState(false);
  const [drafting, setDrafting] = useState(false);
  const [draftError, setDraftError] = useState<string | null>(null);
  const { control, watch, setValue } = form;
  const axios = useAxios();

  const customerRiskWatch = watch("customerRisk", "");
  const customerRiskNoteWatch = watch("customerRiskNote", "");
  const escalationFlagWatch = watch("escalationFlag", "");
  const isSignificant = customerRiskWatch === "Significant Risk";
  const isEscalationYes = String(escalationFlagWatch) === "1";

  const noteHistories = histories.filter(
    (history: any) => history.fieldName === "customerRiskNote"
  );
  const cseNotesHistories = histories.filter(
    (history: any) => history.fieldName === "ccseRiskNote"
  );
  const handleTogglePreviousNotes = () => {
    setTogglePreviousNotes(!togglePreviousNotes);
  };

  const handleCopyNote = (fieldName: string, noteContent: string) => {
    setValue(fieldName, noteContent);
  };

  const latestNote = useMemo(() => {
    return noteHistories.length && noteHistories.length > 0 ? noteHistories[0].newValue : "";
  }, [noteHistories]);
  const cseLatestNote =
    cseNotesHistories.length && cseNotesHistories.length > 0 ? cseNotesHistories[0].newValue : "";

  function extractText(html: string) {
    const div = document.createElement("div");
    div.innerHTML = html;

    return div.textContent?.replace(/\n/g, "") ?? "";
  }

  const isPreviousNoteSame = useMemo(() => {
    return extractText(latestNote) === extractText(customerRiskNoteWatch);
  }, [latestNote, customerRiskNoteWatch]);
  const forCSE = (features || []).includes(FEATURE_KEY.UPDATE_CONCIERGE);

  const draftSuccessPlan = async () => {
    const payload = {
      accountInfoTypes: ["Account Details", "Success Plan Details", "My Notes"],
      extraParams: {
        accountKey: spState.account?.accountKey,
      },
      promptName: "Draft Success Plan Risk Assessment Notes",
    };
    const resp = await axios.post("/api/csdigital/ai-assistant/v1/ai/data-query/summary", payload);
    return resp.data.summary;
  };

  const handleDraft = async (field: string) => {
    setDrafting(true);
    setDraftError(null);
    try {
      const summary = await draftSuccessPlan();
      const mdToHtml = await markdownToHtml(summary);
      setValue(field, mdToHtml);
    } catch (error) {
      console.error("Error drafting success plan:", error);
      setDraftError("Something went wrong while drafting the notes. Please try again later.");
    } finally {
      setDrafting(false);
    }
  };

  return (
    <div className={styles["step-wrapper"]}>
      <div className={styles["grid-two-lane"]}>
        <div className={styles["form-field-wrapper"]}>
          <Typography className={styles["form-field-label"]}>
            Overall Customer Health Assessment:
          </Typography>
          <Typography className={styles["form-field-star"]}>*</Typography>
        </div>
        <RiskSelectController
          control={control}
          name="customerRisk"
          riskItems={riskItems}
          required={true}
        />

        <div className={styles["form-field-wrapper"]}>
          <Typography className={styles["form-field-label"]}>De-Risk Strategies:</Typography>
        </div>
        <DeRiskMultipleController
          control={control}
          name="deRiskStrategies"
        />

        <div className={`${styles["form-field-wrapper"]} ${styles["grid-column-span-2"]}`}>
          <Typography className={styles["form-field-label"]}>
            Explain why the risk is assessed at this level:
          </Typography>
          <Typography className={styles["form-field-star"]}>*</Typography>
        </div>
        <div className={styles["grid-column-span-2"]}>
          <TinyMCEController
            name="customerRiskNote"
            control={control}
            required={true}
          />
        </div>
        <div className={`${styles["flex-space"]} ${styles["grid-column-span-2"]}`}>
          <div
            className={styles["flex-space"]}
            style={{ gap: "8px" }}
          >
            <MButton
              buttonType="blue"
              onClick={() => handleDraft("customerRiskNote")}
              disabled={drafting}
            >
              Draft My Notes
            </MButton>
            {noteHistories.length > 0 && (
              <div className={styles["flex-space"]}>
                <MButton
                  buttonType="blue"
                  onClick={() => handleCopyNote("customerRiskNote", latestNote)}
                  disabled={drafting}
                >
                  Copy Previous Note
                </MButton>
              </div>
            )}

            {drafting && (
              <>
                <CircularProgress size={16} />
                <span className={styles["form-field-italic-label"]}>drafting success plan ...</span>
              </>
            )}
            {draftError && (
              <span
                className={styles["form-field-italic-label"]}
                style={{ color: "red" }}
              >
                {draftError}
              </span>
            )}
          </div>

          {noteHistories.length > 0 && (
            <Button
              onClick={handleTogglePreviousNotes}
              disableRipple
              disableTouchRipple
              disableFocusRipple
              sx={{
                textTransform: "none",
                ":hover": { backgroundColor: "transparent" },
                fontSize: "12px",
              }}
            >
              {togglePreviousNotes ? "Hide Previous Notes" : "View Previous Notes"}
            </Button>
          )}
        </div>
        <div className={`${styles["grid-column-span-2"]}`}>
          {isPreviousNoteSame && (
            <Controller
              control={control}
              name="certifyFlag"
              render={({ field, fieldState }) => (
                <div
                  style={{
                    display: "flex",
                    gap: "8px",
                    alignItems: "center",
                  }}
                >
                  <Checkbox
                    {...field}
                    checked={field.value === 1}
                    onChange={(_, checked) => {
                      field.onChange(checked ? 1 : 0);
                    }}
                    size="small"
                  />
                  <Typography
                    className={styles["form-field-italic-label"]}
                    style={{
                      color: !!fieldState.error ? "red" : "#878787",
                    }}
                  >
                    I certify that the previous note is a current and correct reflection of the
                    customer status.
                  </Typography>
                </div>
              )}
              rules={{
                validate: (value) => {
                  if (isPreviousNoteSame && value !== 1) {
                    return "You must certify that the previous note is current and correct.";
                  }
                  return true;
                },
              }}
            />
          )}
        </div>
        <div className={`${styles["grid-column-span-2"]} ${styles["flex-vertical-gap"]}`}>
          {togglePreviousNotes && <HistoryListRenderer noteHistories={noteHistories} />}
        </div>

        {isSignificant && (
          <div className={`${styles["grid-column-span-2"]} ${styles["flex-vertical-gap"]}`}>
            <Typography className={styles["form-field-label"]}>
              Risk has been assessed as Significant. Please explain what the CS and Account Team are
              doing to address the risk:
            </Typography>
            <TinyMCEController
              name="significantDoing"
              control={control}
            />
          </div>
        )}
        {
          <>
            <div className={styles["form-field-wrapper"]}>
              <Typography className={styles["form-field-label"]}>
                Are there any concerning customer escalations?
              </Typography>
            </div>
            <Controller
              control={control}
              name="escalationFlag"
              render={({ field }) => (
                <Autocomplete
                  {...field}
                  disableClearable
                  options={YNOptions}
                  value={YNOptions.find((o) => o.value === field.value)}
                  onBlur={field.onBlur}
                  onChange={(_, option) => field.onChange(option?.value || "")}
                  getOptionLabel={(o: any) => o.label || ""}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      size="small"
                      variant="outlined"
                      placeholder="Select One"
                      sx={{ width: "240px" }}
                    />
                  )}
                />
              )}
            />
          </>
        }
        {isEscalationYes && (
          <div className={`${styles["grid-column-span-2"]} ${styles["flex-vertical-gap"]}`}>
            <Typography className={styles["form-field-label"]}>Please explain:</Typography>
            <TinyMCEController
              name="escalationNote"
              control={control}
            />
          </div>
        )}

        {forCSE && (
          <>
            <div className={styles["form-field-wrapper"]}>
              <Typography className={styles["form-field-label"]}>CSE Assessment:</Typography>
              {/* <Typography className={styles["form-field-star"]}>*</Typography> */}
            </div>
            <RiskSelectController
              control={control}
              name="ccseRisk"
              riskItems={riskItems}
              required={false}
            />

            <div className={`${styles["form-field-wrapper"]} ${styles["grid-column-span-2"]}`}>
              <Typography className={styles["form-field-label"]}>
                Explain why the risk is assessed at this level:
              </Typography>
            </div>
            <div className={styles["grid-column-span-2"]}>
              <TinyMCEController
                name="ccseRiskNote"
                control={control}
              />
            </div>
            <div className={`${styles["flex-space"]} ${styles["grid-column-span-2"]}`}>
              <div className={styles["flex-space"]}>
                <MButton
                  buttonType="blue"
                  onClick={() => handleCopyNote("ccseRiskNote", cseLatestNote)}
                >
                  Copy Previous Note
                </MButton>
              </div>

              {cseNotesHistories.length > 0 && (
                <Button
                  onClick={() => setToggleCSENotes(!toggleCSENotes)}
                  disableRipple
                  disableTouchRipple
                  disableFocusRipple
                  sx={{
                    textTransform: "none",
                    ":hover": { backgroundColor: "transparent" },
                  }}
                >
                  {toggleCSENotes ? "Hide Previous Notes" : "View Previous Notes"}
                </Button>
              )}
            </div>
            <div className={`${styles["grid-column-span-2"]} ${styles["flex-vertical-gap"]}`}>
              {toggleCSENotes && <HistoryListRenderer noteHistories={cseNotesHistories} />}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default CustomerAssessmentStep;
